import { useAuth } from "@clerk/react"
import { createORPCClient } from "@orpc/client"
import { RPCLink } from "@orpc/client/websocket"
import ReconnectingWebSocket from "partysocket/ws"
import type { ReactNode } from "react"
import { createContext, useContext, useEffect, useRef, useState } from "react"
import { getMainWsUrl, type MainOrpcClient } from "./client"

/**
 * Wraps an oRPC client proxy to prevent React from mistaking it for a Promise.
 */
function wrapClientProxy<T extends object>(client: T): T {
	return new Proxy(client, {
		get(target, prop, receiver) {
			if (prop === "then") {
				return undefined
			}
			const value = Reflect.get(target, prop, receiver)
			if (value && typeof value === "object") {
				return wrapClientProxy(value)
			}
			return value
		},
	})
}

interface MainOrpcWsContextValue {
	client: MainOrpcClient | null
	isAuthReady: boolean
}

const MainOrpcWsContext = createContext<MainOrpcWsContextValue>({
	client: null,
	isAuthReady: false,
})

/**
 * Provider that creates a single, persistent WebSocket connection to the main
 * oRPC router (/ws). Mount once at the app root (e.g., AuthenticatedApp).
 *
 * The WebSocket is created inside a useEffect so that React 18 Strict Mode's
 * unmount/remount cycle correctly tears down the old socket and creates a new
 * one, rather than leaving a closed socket behind.
 */
export function MainOrpcWsProvider({ children }: { children: ReactNode }) {
	const { getToken: clerkGetToken, isLoaded, isSignedIn } = useAuth()
	const isAuthReady = isLoaded && isSignedIn

	// Keep getToken in a ref so the effect doesn't depend on it.
	const getTokenRef = useRef(clerkGetToken)
	getTokenRef.current = clerkGetToken

	const [client, setClient] = useState<MainOrpcClient | null>(null)

	useEffect(() => {
		const wsUrl = getMainWsUrl()

		const websocket = new ReconnectingWebSocket(
			async () => {
				const token = await getTokenRef.current()
				const url = new URL(wsUrl)
				if (token) {
					url.searchParams.set("token", token)
				}
				return url.toString()
			},
			[],
			{
				maxRetries: Number.POSITIVE_INFINITY,
				connectionTimeout: 10_000,
				maxReconnectionDelay: 30_000,
			},
		)

		const link = new RPCLink({
			websocket: websocket as unknown as WebSocket,
		})

		const orpcClient = createORPCClient(link)
		// Wrap in arrow function to prevent React from treating the proxy
		// as a functional updater (oRPC client is a Proxy of a function)
		setClient(() => wrapClientProxy(orpcClient) as MainOrpcClient)

		return () => {
			websocket.close()
		}
	}, [])

	return (
		<MainOrpcWsContext.Provider value={{ client, isAuthReady }}>
			{children}
		</MainOrpcWsContext.Provider>
	)
}

/**
 * Hook to access the shared main oRPC WebSocket client.
 * Must be used within a MainOrpcWsProvider.
 */
export function useMainOrpcWs() {
	return useContext(MainOrpcWsContext)
}
